import { PromoItemStatuses, StreakSheetModes } from '../types';
import type {
  Promo,
  PromoItemEntry,
  PromoItemsResponse,
  PromoItemStatus,
  ResolveDisplayDaysParams,
  StreakDay,
  StreakSheetMode,
  StreakViewState,
} from '../types';

/** Any unusable input collapses to this. Shared with the guest read model. */
export const HIDDEN_STATE: StreakViewState = {
  isVisible: false,
  days: [],
  currentDayNumber: 1,
  isFinalDay: false,
  canClaim: false,
  isWaiting: true,
  heroDay: null,
  sheetMode: StreakSheetModes.Waiting,
  nextClaimAt: null,
  prizeName: null,
};

export const deriveStreakState = (
  promo: Promo | null | undefined,
  itemsResponse: PromoItemsResponse | null | undefined
): StreakViewState => {
  if (!promo || !itemsResponse?.items?.length) return HIDDEN_STATE;

  const entriesInDayOrder = [...itemsResponse.items].sort(compareByPeriodIndex);
  const streakDays = entriesInDayOrder.map(toStreakDay);
  const dayStatuses = streakDays.map((streakDay) => streakDay.status);
  const totalDays = streakDays.length;
  const lastDayIndex = totalDays - 1;
  const prizeEntry = entriesInDayOrder[lastDayIndex];

  const sheetMode = resolveSheetMode(promo, itemsResponse, dayStatuses);
  const isWaiting = sheetMode === StreakSheetModes.Waiting;
  const isRestart = sheetMode === StreakSheetModes.Restart;
  const isCompleted = sheetMode === StreakSheetModes.Completed;
  const canClaim = !isWaiting && !isCompleted;

  const finishedDaysCount = dayStatuses.filter(isCompletedStatus).length;
  const progressDaysCount = isRestart ? 0 : finishedDaysCount;
  const currentDayNumber = isCompleted
    ? totalDays
    : Math.max(1, progressDaysCount + (canClaim ? 1 : 0));

  const displayDays = resolveDisplayDays({ streakDays, sheetMode });

  const heroDay = canClaim
    ? displayDays[currentDayNumber - 1] ?? null
    : displayDays[Math.min(currentDayNumber, lastDayIndex)] ?? null;

  const isFinalDay = currentDayNumber >= totalDays;

  return {
    isVisible: true,
    days: displayDays,
    currentDayNumber,
    isFinalDay,
    canClaim,
    isWaiting,
    heroDay,
    sheetMode,
    nextClaimAt: isWaiting
      ? deriveNextClaimAt(
          promo,
          finishedDaysCount,
          entriesInDayOrder[0].claimedAt
        )
      : null,
    prizeName: prizeEntry.item.itemName,
  };
};

const compareByPeriodIndex = (
  first: PromoItemEntry,
  second: PromoItemEntry
): number => first.item.periodIndex - second.item.periodIndex;

const toStreakDay = (entry: PromoItemEntry): StreakDay => ({
  dayNumber: entry.item.periodIndex,
  status: entry.status,
  creditAmount: entry.item.creditAmount,
  rewardName: entry.item.itemName,
});
export const isCompletedStatus = (status: PromoItemStatus): boolean =>
  status === PromoItemStatuses.Claimed || status === PromoItemStatuses.Valid;

export const isActiveStatus = (status: PromoItemStatus): boolean =>
  status === PromoItemStatuses.Active;

const hasUserJoined = (promo: Promo, dayStatuses: PromoItemStatus[]): boolean =>
  promo.userParticipatedAt !== null ||
  dayStatuses.some((status) => status !== PromoItemStatuses.Locked);

const hasClaimableDay = (dayStatuses: PromoItemStatus[]): boolean =>
  dayStatuses.some(isActiveStatus);

const resolveDisplayDays = ({
  streakDays,
  sheetMode,
}: ResolveDisplayDaysParams): StreakDay[] => {
  if (sheetMode === StreakSheetModes.Restart)
    return streakDays.map((streakDay, dayIndex) => ({
      ...streakDay,
      status:
        dayIndex === 0 ? PromoItemStatuses.Active : PromoItemStatuses.Locked,
    }));
  if (sheetMode === StreakSheetModes.Completed)
    return streakDays.map((streakDay) => ({
      ...streakDay,
      status: PromoItemStatuses.Claimed,
    }));
  return streakDays;
};

const resolveSheetMode = (
  promo: Promo,
  itemsResponse: PromoItemsResponse,
  dayStatuses: PromoItemStatus[]
): StreakSheetMode => {
  if (itemsResponse.restartRequired) return StreakSheetModes.Restart;
  if (itemsResponse.journeyCompleted && !hasClaimableDay(dayStatuses))
    return StreakSheetModes.Completed;
  if (!hasUserJoined(promo, dayStatuses)) return StreakSheetModes.NotJoined;
  if (hasClaimableDay(dayStatuses)) return StreakSheetModes.Normal;
  return StreakSheetModes.Waiting;
};

const deriveNextClaimAt = (
  promo: Promo,
  finishedDaysCount: number,
  dayOneClaimedAt: string | null
): string | null => {
  if (!promo.userParticipatedAt || finishedDaysCount === 0) return null;

  const periodMs = promo.giftAvailabilitySeconds * 1000;
  const participatedAtMs = new Date(promo.userParticipatedAt).getTime();
  const dayOneClaimedAtMs = dayOneClaimedAt
    ? new Date(dayOneClaimedAt).getTime()
    : 0;

  const dayOneDeadlineMs = participatedAtMs + periodMs;
  const hasRestarted = dayOneClaimedAtMs > dayOneDeadlineMs;
  const journeyStartMs = hasRestarted ? dayOneClaimedAtMs : participatedAtMs;
  const nextClaimAtMs = journeyStartMs + finishedDaysCount * periodMs;

  return new Date(nextClaimAtMs).toISOString();
};
